import {
  Home,
  Megaphone,
  User,
  Users,
  FileText,
  Settings,
  ShieldCheck,
  LogOut,
  Rocket,
  BookOpen,
  Building2,
  Eye,
  ShoppingCart,
  MousePointerClick,
  Table2,
} from "lucide-react";
import { NavLink, useLocation } from "react-router-dom";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import bfLogo from "@/assets/beverage-federation-logo-circle.png";
import type { PartnerAccessTier } from "@/lib/hooks/usePartnerAccessTier";

// Mirrors Craft Commerce Hub source: full nav is shown to every authenticated
// portal user. Tier-specific gating is handled inside individual pages, not
// by hiding sidebar entries.
const baseNavItems = [
  { title: "Home", url: "/portal/dashboard", icon: Home, end: true },
  { title: "Go Live Checklist", url: "/portal/go-live", icon: Rocket },
  { title: "Co-Marketing", url: "/portal/co-marketing", icon: Megaphone },
  { title: "Partner Profile", url: "/portal/profile", icon: User },
  { title: "Partner Directory", url: "/portal/directory", icon: Users },
  { title: "Sales (Coming Soon)", url: "/portal/sales", icon: ShoppingCart, comingSoon: true },
  { title: "Reports, Files & Resources", url: "/portal/reports", icon: FileText },
  { title: "Settings", url: "/portal/settings", icon: Settings },
];

const adminItems = [
  { title: "Home — Temp Portal", url: "/portal/temp-preview", icon: Eye },
  { title: "Admin Center", url: "/admin/dashboard", icon: ShieldCheck },
  { title: "Blog", url: "/admin/blog", icon: BookOpen },
  { title: "Contract Directory Admin", url: "/admin/directory", icon: Building2 },
  { title: "Manage Users", url: "/admin/users", icon: Users },
  { title: "Popups & Lead Capture", url: "/admin/popups", icon: MousePointerClick },
  { title: "Master Supplier List", url: "/admin/master-supplier-list", icon: Table2 },
];

interface PortalSidebarProps {
  tier: PartnerAccessTier | null;
  isAdmin: boolean;
  onSignOut: () => void;
}

export function PortalSidebar({ tier, isAdmin, onSignOut }: PortalSidebarProps) {
  const { state } = useSidebar();
  const collapsed = state === "collapsed";
  const location = useLocation();

  // Match the source project: every authenticated portal user sees the full nav.
  // The `tier` prop is preserved for future per-item gating but no longer hides items.
  void tier;
  const navItems = baseNavItems;

  const isActive = (path: string, end?: boolean) => {
    if (end) return location.pathname === path;
    return location.pathname === path || location.pathname.startsWith(path + "/");
  };

  const renderItems = (items: typeof baseNavItems) =>
    items.map((item) => {
      const active = isActive(item.url, item.end);
      const isComingSoon = (item as any).comingSoon;
      if (isComingSoon) {
        return (
          <SidebarMenuItem key={item.title}>
            <SidebarMenuButton asChild>
              <div
                aria-disabled="true"
                className={`flex items-center ${collapsed ? "justify-center px-0 mx-1" : "gap-3 px-4 mx-2"} py-2.5 rounded-lg text-sm cursor-not-allowed`}
                style={{ color: "var(--primary)" }}
              >
                <item.icon className={`${collapsed ? "w-5 h-5" : "w-4 h-4"} flex-shrink-0`} />
                {!collapsed && <span>{item.title}</span>}
              </div>
            </SidebarMenuButton>
          </SidebarMenuItem>
        );
      }
      return (
        <SidebarMenuItem key={item.title}>
          <SidebarMenuButton asChild>
            <NavLink
              to={item.url}
              end={item.end}
              className={`flex items-center ${collapsed ? "justify-center px-0 mx-1" : "gap-3 px-4 mx-2"} py-2.5 rounded-lg text-sm transition-all ${
                active
                  ? "font-semibold shadow-sm"
                  : "text-white/70 hover:bg-white/10 hover:text-white"
              }`}
              style={
                active
                  ? { background: "var(--primary)", color: "var(--navy)" }
                  : undefined
              }
            >
              <item.icon
                className={`${collapsed ? "w-5 h-5" : "w-4 h-4"} flex-shrink-0`}
                style={active ? { color: "var(--navy)" } : undefined}
              />
              {!collapsed && <span>{item.title}</span>}
            </NavLink>
          </SidebarMenuButton>
        </SidebarMenuItem>
      );
    });

  return (
    <Sidebar
      collapsible="icon"
      className="border-r-0"
      style={{ ['--sidebar-width-icon' as any]: '4.5rem' }}
    >
      <SidebarHeader
        className={`h-14 flex items-center justify-center border-b border-white/10 ${collapsed ? "px-2 py-0" : "px-4 py-0"}`}
        style={{ background: "var(--navy)" }}
      >
        <div className={`flex items-center w-full ${collapsed ? "justify-center gap-0" : "gap-3"}`}>
          <img
            src={bfLogo}
            alt="Beverage Federation"
            className={`${collapsed ? "w-9 h-9" : "w-9 h-9"} rounded-full flex-shrink-0 ring-2`}
            style={{ ['--tw-ring-color' as any]: "rgba(247,168,0,0.3)" }}
          />
          {!collapsed && (
            <div>
              <span className="font-bold text-sm text-white block leading-tight">
                Partner Portal
              </span>
              <span className="text-[10px] text-white/50">
                Beverage Federation
              </span>
            </div>
          )}
        </div>
      </SidebarHeader>

      <SidebarContent
        className="text-white pt-2"
        style={{ background: "var(--navy)" }}
      >
        <SidebarGroup>
          <SidebarGroupLabel className="text-white/40 text-[10px] uppercase tracking-widest font-semibold px-4">
            {!collapsed && "Navigation"}
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>{renderItems(navItems)}</SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {isAdmin && (
          <SidebarGroup>
            <SidebarGroupLabel className="text-white/40 text-[10px] uppercase tracking-widest font-semibold px-4">
              {!collapsed && "Administration"}
            </SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>{renderItems(adminItems as any)}</SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}
      </SidebarContent>

      <SidebarFooter
        className="border-t border-white/10 p-2"
        style={{ background: "var(--navy)" }}
      >
        <button
          onClick={onSignOut}
          className={`flex items-center ${collapsed ? "justify-center px-0 mx-1" : "gap-3 px-4 mx-2"} py-2.5 rounded-lg text-white/60 hover:text-white hover:bg-white/10 transition-all w-full text-sm`}
        >
          <LogOut className={`${collapsed ? "w-5 h-5" : "w-4 h-4"} flex-shrink-0`} />
          {!collapsed && <span>Sign Out</span>}
        </button>
      </SidebarFooter>
    </Sidebar>
  );
}

export default PortalSidebar;
